import { useMemo, type RefObject } from 'react';
import { z } from 'zod';
import {
  ReportShapeItem,
  ShapeType,
  FilterType,
  ReportType,
} from '../../../reportsTypes';
import { createReportCloneFormSchema } from '../../utils/createReportCloneFormSchema';
import { createReportTemplatePeriodFormSchema } from '../../utils/createPeriodFormSchema';
import type { GeoJson } from '@ais-ui/api-spec/shapes';

interface Params {
  currentShapeItems: ReportShapeItem[] | undefined;
  shapeType: ShapeType;
  filterType: FilterType;
  reportType: ReportType;
  databaseRange: any; // type from api package not locally available
  messages: Record<string, string>;
  formatDateTimeWithTimeZone: (d: Date | string | number | undefined) => string;
  instantShape?: boolean;
  shapesRef?: RefObject<GeoJson[] | undefined>;
}

export function useQuickCreateSchema({
  currentShapeItems,
  shapeType,
  filterType,
  reportType,
  databaseRange,
  messages,
  formatDateTimeWithTimeZone,
  instantShape = false,
  shapesRef,
}: Params) {
  const baseCloneSchema = useMemo(
    () =>
      createReportCloneFormSchema(
        currentShapeItems,
        shapeType,
        filterType,
        reportType.miscellaneous,
        reportType.ships,
        true,
        instantShape,
        shapesRef,
      ),
    [
      currentShapeItems,
      shapeType,
      filterType,
      reportType.miscellaneous,
      reportType.ships,
      instantShape,
      shapesRef,
    ],
  );

  return useMemo(() => {
    if (!databaseRange) return baseCloneSchema;

    const refined = baseCloneSchema.refine(
      (data: any) => data.end_datetime > data.start_datetime,
      {
        message:
          messages.reportsCloneTimeValidationText ||
          'End time must be after start time',
        path: ['end_datetime'],
      },
    ) as z.ZodTypeAny;

    const startDatetimeThreshold = databaseRange?.database.begin;
    const endDatetimeThreshold = databaseRange?.database.end;

    return createReportTemplatePeriodFormSchema(
      startDatetimeThreshold,
      endDatetimeThreshold,
      formatDateTimeWithTimeZone(startDatetimeThreshold),
      formatDateTimeWithTimeZone(endDatetimeThreshold),
      refined,
    );
  }, [databaseRange, baseCloneSchema, messages, formatDateTimeWithTimeZone]);
}
